/**
 * Pure response-shape parsing for GET /api/crm/admin/name-fix/review
 * (marker nf-review-render-fix-v1).
 *
 * The worker handler (`api/src/index.ts`, app.get('/api/crm/admin/name-fix/review'))
 * responds with `{ status, total, reviews: [...], marker }` — NOT a bare array and
 * NOT a `{ rows: [...] }` wrapper. `src/api.ts`'s `listNameFixReview` previously read
 * `j?.rows ?? []`, which is always `[]` against the real shape, so the "Legacy name
 * fix" admin panel rendered "(0) Nothing pending review" while the server actually
 * had 162 pending rows (reason breakdown: nocomma_swap 98, org_couple 10, note_like 33,
 * household_dupe 18, org_like 2, different_surnames 1) — verified live 2026-07-04.
 *
 * Extracted into a pure helper (rather than left inline in the fetch wrapper) so the
 * exact response-key contract is unit-tested and guards against ever again reading a
 * key the server doesn't send.
 */

/** A single row of the name-fix review queue, as the worker returns it. */
export interface NameFixReviewRow {
  id: number
  entity_type: string
  contact_id: string
  reason: string
  original_name: string
  suggestion_json: string | null
  // The worker pre-parses suggestion_json server-side and attaches it here.
  suggestion?: { proposed?: string | null; persons?: string[] | null; display?: string | null } | null
  sibling_ids: string[] | null
  status: 'pending' | 'accepted' | 'dismissed'
  run_id: string | null
  created_at: string
  resolved_by_email: string | null
  resolved_at: string | null
}

/**
 * Parse the JSON body of GET /api/crm/admin/name-fix/review into a row array.
 *
 * Primary (real) shape: `{ reviews: NameFixReviewRow[], total, status, marker }`.
 * The top-level `reviews` read is the authoritative contract and is checked FIRST,
 * so a correct body can never be regressed by the fallbacks below.
 *
 * Defensive fallbacks — never the primary contract, kept so no plausible response
 * envelope silently renders the queue as empty (the exact "162 rows in the network
 * response, `(0) Nothing pending review` in the panel" symptom):
 *   1. a bare array body,
 *   2. a legacy `{ rows: [...] }` wrapper,
 *   3. an `{ data: ... }` / `{ result: ... }` envelope (some fetch/response wrappers
 *      nest the payload) — either a bare nested array OR a nested `{ reviews|rows }`.
 * A `.data`-enveloped body would otherwise slip past the top-level reads and resolve
 * to `[]`, which is precisely how a fully-populated backlog can look empty.
 */
export function parseReviewResponse(json: unknown): NameFixReviewRow[] {
  if (Array.isArray(json)) return json as NameFixReviewRow[]
  if (json && typeof json === 'object') {
    const body = json as Record<string, unknown>
    if (Array.isArray(body.reviews)) return body.reviews as NameFixReviewRow[]
    if (Array.isArray(body.rows)) return body.rows as NameFixReviewRow[]
    // Defensive: unwrap a single `{ data }` / `{ result }` envelope, then re-read.
    const nested = body.data ?? body.result
    if (Array.isArray(nested)) return nested as NameFixReviewRow[]
    if (nested && typeof nested === 'object') {
      const n = nested as Record<string, unknown>
      if (Array.isArray(n.reviews)) return n.reviews as NameFixReviewRow[]
      if (Array.isArray(n.rows)) return n.rows as NameFixReviewRow[]
    }
  }
  return []
}

/**
 * Parse the `suggestion_json` column (or the server's pre-parsed `suggestion` field,
 * if present on the row) into a one-line human summary for the review-queue list —
 * e.g. "Betancourt Jeffery" for a Tier A swap or "Alan & Karen Dorman" for a Tier B
 * household split. Returns '' when there's no auto-applicable suggestion (e.g.
 * household_dupe / note_like rows the office resolves manually).
 */
export function summarizeNameFixSuggestion(row: {
  suggestion_json?: string | null
  suggestion?: { proposed?: string | null; persons?: string[] | null; display?: string | null } | null
}): string {
  let s = row.suggestion ?? null
  if (!s && row.suggestion_json) {
    try { s = JSON.parse(row.suggestion_json) } catch { s = null }
  }
  if (!s) return ''
  if (s.display) return s.display
  if (Array.isArray(s.persons) && s.persons.length) return s.persons.join(' & ')
  if (s.proposed) return s.proposed
  return ''
}
